<template>
  <v-dialog
    v-model="dialog"
    max-width="720"
    scrollable
    persistent
  >
    <v-card>
      <v-toolbar
        dark
        flat
        color="secondary"
      >
        <v-btn
          icon
          dark
          @click="close"
        >
          <v-icon>close</v-icon>
        </v-btn>
        <v-toolbar-title>צ'אט בדיקה — {{ partnerName }}</v-toolbar-title>
        <v-spacer />
        <v-btn
          icon
          dark
          :loading="loadingMessages"
          @click="loadMessages"
        >
          <v-icon>refresh</v-icon>
        </v-btn>
      </v-toolbar>

      <v-card-text
        class="partner-check-chat-messages pa-3"
        style="min-height: 220px; max-height: 420px; overflow-y: auto;"
      >
        <div
          v-if="loadingMessages && !messages.length"
          class="text-xs-center py-4 grey--text"
        >
          טוען הודעות…
        </div>
        <div
          v-else-if="!messages.length"
          class="text-xs-center py-4 grey--text"
        >
          אין הודעות עדיין. שלחו הודעה למטה.
        </div>
        <div
          v-for="msg in messages"
          :key="msg._id"
          class="mb-3"
          :class="msg.direction === 'inbound' ? 'partner-check-chat-inbound' : 'partner-check-chat-outbound'"
        >
          <div class="caption grey--text mb-1">
            <v-icon
              x-small
              class="mr-1"
            >
              {{ msg.channel === 'whatsapp' ? 'chat' : 'email' }}
            </v-icon>
            {{ formatMessageMeta(msg) }}
          </div>
          <div
            class="partner-check-chat-bubble pa-2"
            :class="msg.direction === 'inbound' ? 'inbound-bubble' : 'outbound-bubble'"
          >
            <pre class="partner-check-chat-body">{{ msg.body }}</pre>
          </div>
        </div>
      </v-card-text>

      <v-divider />

      <v-card-text class="py-2 px-3">
        <div
          v-if="!selectedPhone"
          class="caption red--text mb-2"
        >
          לא נמצא טלפון לשליחה ב-WhatsApp
        </div>
        <v-layout
          align-center
          row
          wrap
        >
          <v-flex
            xs12
            sm9
          >
            <v-text-field
              v-model="composeText"
              outline
              hide-details
              label="הודעה"
              placeholder="הקלידו הודעה…"
              :disabled="sending || !selectedPhone"
              @keydown.enter.prevent="sendCompose"
            />
          </v-flex>
          <v-flex
            xs12
            sm3
            class="text-xs-left text-sm-center"
          >
            <v-btn
              color="secondary"
              dark
              block
              :loading="sending"
              :disabled="!canSendCompose"
              @click="sendCompose"
            >
              <v-icon left>send</v-icon>
              שליחה
            </v-btn>
          </v-flex>
        </v-layout>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import { format } from "date-fns";
import { getApi } from "@/api";

export default {
  props: {
    value: {
      type: Boolean,
      default: false
    },
    partner: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      messages: [],
      loadingMessages: false,
      loadingPartner: false,
      sending: false,
      composeText: "",
      selectedPhone: null,
      selectedEmail: null,
      pollTimer: null
    };
  },
  computed: {
    dialog: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
      }
    },
    partnerName() {
      return (this.partner && this.partner.name) || "ספק";
    },
    canSendCompose() {
      return !!this.selectedPhone && !!this.composeText.trim() && !this.sending;
    }
  },
  watch: {
    value(isOpen) {
      if (isOpen && this.partner && this.partner._id) {
        this.initDialog();
        this.startPolling();
      } else {
        this.stopPolling();
      }
    },
    "partner._id"(id) {
      if (this.value && id) {
        this.initDialog();
      }
    }
  },
  methods: {
    format,
    close() {
      this.stopPolling();
      this.dialog = false;
    },
    startPolling() {
      this.stopPolling();
      this.pollTimer = setInterval(() => {
        if (this.value && this.partner && this.partner._id) {
          this.loadMessages({ silent: true });
        }
      }, 10000);
    },
    stopPolling() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer);
        this.pollTimer = null;
      }
    },
    async initDialog() {
      this.composeText = "";
      await this.ensurePartnerContacts();
      this.applyDefaultSelections();
      await this.loadMessages();
    },
    formatMessageMeta(msg) {
      const when = msg.created ? format(new Date(msg.created), "DD/MM/YYYY HH:mm") : "";
      const dir = msg.direction === "inbound" ? "נכנס" : "יוצא";
      const channel = msg.channel === "whatsapp" ? "WhatsApp" : "אימייל";
      const to = msg.recipient ? ` · ${msg.recipient}` : "";
      return `${when} · ${dir} · ${channel}${to}`;
    },
    async ensurePartnerContacts() {
      if (!this.partner || !this.partner._id) return;
      this.loadingPartner = true;
      try {
        await this.$store.dispatch("API_GET", {
          url: "/partners/" + this.partner._id,
          entityType: "partners",
          schema: "partnerListSchema"
        });
      } catch (err) {
        console.error(err);
      } finally {
        this.loadingPartner = false;
      }
    },
    getPartnerEntity() {
      if (!this.partner) return null;
      return (
        this.$store.state.entities.partners[this.partner._id] || this.partner
      );
    },
    getContactDetails() {
      const partner = this.getPartnerEntity();
      if (!partner) return [];

      const details = [];
      (partner.contactDetails || []).forEach(id => {
        const detail =
          typeof id === "string"
            ? this.$store.state.entities.contactDetails[id]
            : id;
        if (detail) details.push(detail);
      });

      (partner.contacts || []).forEach(contactId => {
        const contact =
          typeof contactId === "string"
            ? this.$store.state.entities.contacts[contactId]
            : contactId;
        if (!contact) return;
        if (contact.phone) {
          details.push({ data: contact.phone, type: null, _phoneHint: true });
        }
        (contact.contactDetails || []).forEach(detailId => {
          const detail =
            typeof detailId === "string"
              ? this.$store.state.entities.contactDetails[detailId]
              : detailId;
          if (detail) details.push(detail);
        });
      });

      return details;
    },
    isPhoneDetail(detail) {
      if (!detail || !detail.data) return false;
      if (detail._phoneHint) return true;
      const type = this.$store.state.entities.contactDetailTypes[detail.type];
      if (type && type.formatAsPhoneNumber) return true;
      const cleaned = String(detail.data).replace(/[^\d+]/g, "");
      return cleaned.length >= 8 && !String(detail.data).includes("@");
    },
    isEmailDetail(detail) {
      if (!detail || !detail.data) return false;
      const type = this.$store.state.entities.contactDetailTypes[detail.type];
      const title = ((type && type.title) || "").toLowerCase();
      if (title.includes("מייל") || title.includes("email") || title.includes("mail")) {
        return true;
      }
      return /.+@.+\..+/.test(String(detail.data));
    },
    normalizePhone(phone) {
      return String(phone || "").replace(/[^\d]/g, "");
    },
    collectPhones() {
      const phones = [];
      this.getContactDetails().forEach(detail => {
        if (!this.isPhoneDetail(detail)) return;
        const phone = this.normalizePhone(detail.data);
        if (phone && phones.indexOf(phone) === -1) {
          phones.push(phone);
        }
      });
      return phones;
    },
    collectEmails() {
      const emails = [];
      this.getContactDetails().forEach(detail => {
        if (!this.isEmailDetail(detail)) return;
        const email = String(detail.data).trim().toLowerCase();
        if (email && emails.indexOf(email) === -1) {
          emails.push(email);
        }
      });
      return emails;
    },
    applyDefaultSelections() {
      const phones = this.collectPhones();
      const emails = this.collectEmails();
      this.selectedPhone = phones.length ? phones[0] : null;
      this.selectedEmail = emails.length ? emails[0] : null;
    },
    async loadMessages(options = {}) {
      const silent = options.silent === true;
      if (!this.partner || !this.partner._id) return;
      if (!silent) {
        this.loadingMessages = true;
      }
      try {
        const api = await getApi();
        const { data } = await api.get(
          `/partners/${this.partner._id}/check-messages`,
          { params: { limit: 200 } }
        );
        const next = (data && data.results) || [];
        const prevCount = this.messages.length;
        this.messages = next;
        if (next.length !== prevCount || !silent) {
          this.$nextTick(this.scrollToBottom);
        }
        if (next.length > prevCount) {
          this.$emit("updated", this.partner._id);
        }
        await this.markChatRead();
      } catch (err) {
        if (!silent) {
          this.$store.dispatch("SHOW_SNACKBAR", {
            text: err.response ? err.response.data.message : err.message,
            color: "error"
          });
        }
      } finally {
        if (!silent) {
          this.loadingMessages = false;
        }
      }
    },
    scrollToBottom() {
      const el = this.$el && this.$el.querySelector(".partner-check-chat-messages");
      if (el) {
        el.scrollTop = el.scrollHeight;
      }
    },
    appendMessage(msg) {
      if (!msg || !msg._id) return;
      if (this.messages.some(item => item._id === msg._id)) return;
      this.messages = [...this.messages, msg];
      this.$nextTick(this.scrollToBottom);
      if (this.value && this.partner && this.partner._id) {
        this.markChatRead();
      }
    },
    async markChatRead() {
      if (!this.partner || !this.partner._id) return;
      try {
        const api = await getApi();
        await api.post(`/partners/${this.partner._id}/check-messages/read`);
        this.$emit("updated", this.partner._id);
      } catch (err) {
        console.error(err);
      }
    },
    onPartnerCheckMessage(payload) {
      if (!payload || !this.partner || payload.partnerId !== this.partner._id) return;
      if (payload.message) {
        this.appendMessage(payload.message);
      } else {
        this.loadMessages();
      }
      this.$emit("updated", this.partner._id);
    },
    async sendCompose() {
      if (!this.canSendCompose || !this.partner || !this.partner._id) return;
      const text = this.composeText.trim();
      this.sending = true;
      try {
        const api = await getApi();
        const response = await api.post(
          `/partners/${this.partner._id}/send-chat-whatsapp`,
          {
            phone: this.selectedPhone,
            message: text
          }
        );
        const isDryRun = response.data.whatsapp && response.data.whatsapp.dryRun;
        this.$store.dispatch("SHOW_SNACKBAR", {
          text: isDryRun
            ? "WhatsApp dry-run — ההודעה לא נשלחה בפועל"
            : "הודעה נשלחה",
          color: isDryRun ? "warning" : "success"
        });
        this.composeText = "";
        await this.loadMessages();
        this.$emit("updated", this.partner._id);
      } catch (err) {
        this.$store.dispatch("SHOW_SNACKBAR", {
          text: err.response ? err.response.data.message : err.message,
          color: "error"
        });
      } finally {
        this.sending = false;
      }
    }
  },
  sockets: {
    partner_check_message(payload) {
      this.onPartnerCheckMessage(payload);
    }
  },
  beforeDestroy() {
    this.stopPolling();
  }
};
</script>

<style scoped>
.partner-check-chat-body {
  white-space: pre-wrap;
  word-break: break-word;
  font-family: inherit;
  font-size: 14px;
  margin: 0;
}
.inbound-bubble {
  background: #e8f5e9;
  border-radius: 8px 8px 8px 2px;
  max-width: 92%;
}
.outbound-bubble {
  background: #e3f2fd;
  border-radius: 8px 8px 2px 8px;
  max-width: 92%;
  margin-right: auto;
  margin-left: 0;
}
.partner-check-chat-outbound {
  text-align: left;
}
.partner-check-chat-inbound {
  text-align: right;
}
</style>



// WEBPACK FOOTER //
// src/components/partners/PartnerCheckChatDialog.vue